import {isEqual} from 'lodash';
import React, {
  ElementRef, forwardRef, ForwardRefRenderFunction, memo, ReactNode, useCallback, useEffect, useId,
  useImperativeHandle, useRef
} from 'react';
import {createRoot, Root} from 'react-dom/client';
import {ExtendedFloatPaneOptions as FloatPaneOptions, FloatPaneView} from '@/map/modules';
import {FloatPane as OverlayView} from '@/map/modules/classes';
import {QueryClientProvider, useQueryClient} from '@tanstack/react-query';
import {isEqualPointPosition, isEqualPositionTransform} from '../utils';

interface IProps extends Omit<FloatPaneOptions, 'content'> {
  children?: React.ReactNode;
}

interface IHandle {
  refresh(): void;
}

const ForwardRefRenderComponent: ForwardRefRenderFunction<IHandle, IProps> = (
  {map, positionInfo, zIndex, draggable, children},
  ref
) => {
  const instance = useRef<FloatPaneView>();
  const uid = useId();
  const prevRef = useRef<{
    positionInfo?: IProps['positionInfo'];
    zIndex?: IProps['zIndex'];
    content?: ReactNode;
    root?: Root;
  }>({});

  const queryClient = useQueryClient();

  const clear = React.useCallback(() => {
    if (instance.current) {
      instance.current.setMap?.(null);
      instance.current = undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance.current]);

  const createOverlay = useCallback(() => {
    clear();

    prevRef.current = {
      positionInfo,
      zIndex,
    };

    instance.current = new (OverlayView as any)({
      map,
      positionInfo,
      zIndex,
      draggable,
      content: `<div id="${uid}"></div>`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, positionInfo, uid, clear]);

  const updateContent = React.useCallback(() => {
    const container = document.getElementById(uid);

    if (container) {
      if (!prevRef.current.root) {
        prevRef.current.root = createRoot(container);
      }
      prevRef.current.root.render(
        <QueryClientProvider client={queryClient}>
          <>{children}</>
        </QueryClientProvider>
      );
      prevRef.current.content = children;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, children]);

  const refresh = React.useCallback(() => {
    createOverlay();
    updateContent();
  }, [createOverlay, updateContent]);

  useEffect(() => {
    if (map && positionInfo) {
      createOverlay();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    if (instance.current && !isEqual(children, prevRef.current.content)) {
      updateContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  useEffect(() => {
    if (instance.current && prevRef.current.zIndex !== zIndex) {
      instance.current.setZIndex(zIndex);
      prevRef.current.zIndex = zIndex;
    }
  }, [zIndex]);

  useEffect(() => {
    if (
      instance.current &&
      (!isEqualPointPosition(
        prevRef.current.positionInfo?.position,
        positionInfo?.position
      ) ||
        !isEqualPositionTransform(
          prevRef.current.positionInfo?.transform,
          positionInfo?.transform
        ))
    ) {
      instance.current.setPosition(positionInfo);
      prevRef.current.positionInfo = positionInfo;
    }
  }, [positionInfo]);

  useEffect(() => {
    return () => {
      clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    refresh,
  }));

  return null;
};

const ForwardRefComponent = forwardRef(ForwardRefRenderComponent);

export type FloatPaneHandle = ElementRef<typeof FloatPane>;

export const FloatPane = memo(ForwardRefComponent, (prev, next) => {
  if (
    prev.map !== next.map ||
    !isEqual(prev.children, next.children) ||
    !isEqualPointPosition(
      prev.positionInfo?.position,
      next.positionInfo?.position
    ) ||
    isEqualPositionTransform(
      prev.positionInfo?.transform,
      next.positionInfo?.transform
    ) ||
    prev.zIndex !== next.zIndex
  ) {
    return false;
  }

  return true;
});
